/** Interface texts (Spanish first, docs/02 RNF; externalised per CLAUDE.md). */
export const t = {
  app: {
    title: 'flow',
    loading: 'Cargando…',
    openingDatabase: 'Abriendo la base de datos local…',
    startup: (dbMs: number, listMs: number) => `arranque: base ${dbMs} ms · bloques ${listMs} ms`,
  },
  login: {
    title: 'Iniciar sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    submit: 'Entrar',
    submitting: 'Entrando…',
    invalidCredentials: 'Correo o contraseña incorrectos.',
    tooManyAttempts: 'Demasiados intentos. Espera un minuto y vuelve a probar.',
    networkError: 'No se ha podido contactar con el servidor. Comprueba la conexión.',
  },
  session: {
    signOut: 'Cerrar sesión',
    signedInAs: (email: string) => `Sesión de ${email}`,
  },
  nav: {
    home: 'Bloques',
    blocksScreen: 'Lista de prueba',
    search: 'Buscar',
    capture: 'Cámara y micrófono (prueba)',
    perf: 'Medición',
  },
  quickCapture: {
    title: 'Capturar',
    open: 'Capturar',
    cancel: 'Cancelar',
    save: 'Guardar',
    field: 'Qué quieres capturar',
    placeholder: 'Escribe lo que tienes en la cabeza: «Llamar a Marta mañana @llamadas»',
    detected: 'Detectado',
    remove: (label: string, value: string) => `Quitar ${label.toLowerCase()}: ${value}`,
    newTag: 'nuevo',
    saveIn: 'Guardar en',
    keepOpen: 'Seguir capturando',
    hint: 'Se guarda al instante, también sin conexión. Lo clasificarás después en el triaje.',
    announce: (items: string[]) => `Detectado: ${items.join(', ')}`,
    due: 'Vence',
    start: 'Empieza',
    tagLabel: { '@': 'Contexto', '#': 'Tema', '+': 'Persona' } as const,
    weekdays: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    months: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    error: 'No se ha podido guardar la captura. Vuelve a intentarlo.',
  },
  shell: {
    navigation: 'Navegación principal',
    openMenu: 'Abrir la navegación',
    closeMenu: 'Cerrar la navegación',
    collapseSidebar: 'Plegar la barra lateral',
    expandSidebar: 'Desplegar la barra lateral',
    loadingWorkspace: 'Personal',
    openPalette: 'Buscar y comandos',
    testScreens: 'Pantallas de prueba',
    badgeOverflow: '99+',
    overdue: (count: string) => (count === '1' ? '1 vencido' : `${count} vencidos`),
    sections: {
      flow: 'Flujo',
      organization: 'Organización',
      rituals: 'Rituales',
    },
    viewNames: {
      inbox: 'Inbox',
      asap: 'ASAP',
      backlog: 'Backlog',
      waiting: 'Seguimiento',
      projects: 'Proyectos',
      resources: 'Recursos',
    } as Record<string, string>,
    commands: {
      goTo: 'Ir a',
      actions: 'Acciones',
      themeLight: 'Tema claro',
      themeDark: 'Tema oscuro',
      themeSystem: 'Tema del sistema',
      toggleSidebar: 'Plegar o desplegar la barra lateral',
      shortcuts: 'Atajos de teclado',
      capture: 'Capturar',
      triage: 'Procesar Inbox',
    },
    palette: {
      title: 'Buscar y comandos',
      placeholder: 'Escribe una vista, una acción o lo que buscas…',
      empty: 'Nada coincide.',
      search: (term: string) => `Buscar «${term}»`,
      close: 'Cerrar la paleta',
    },
    shortcuts: {
      title: 'Atajos de teclado',
      closeButton: 'Cerrar',
      palette: 'Buscar y comandos',
      capture: 'Capturar',
      goTo: (view: string) => `Ir a ${view}`,
      help: 'Esta chuleta',
      close: 'Cerrar paneles y diálogos',
    },
  },
  inbox: {
    list: 'Inbox',
    process: 'Procesar Inbox',
    empty: 'Inbox vacío. Buen trabajo.',
    count: (n: number) => (n === 1 ? '1 elemento' : `${n} elementos`),
    due: (day: string) => `vence ${day}`,
  },
  triage: {
    title: 'Procesar Inbox',
    subtitle: 'una captura cada vez',
    exit: 'Salir del triaje',
    of: (position: number, total: string) => `${position} de ${total}`,
    progress: 'Progreso del triaje',
    queue: 'Cola del triaje',
    processed: 'Procesadas',
    current: 'Ahora',
    next: 'Siguientes',
    more: (n: number) => `y ${n} más`,
    question: '¿Qué es exactamente?',
    edit: 'Editar el título',
    editField: 'Título del elemento',
    assigned: 'Ya tiene',
    detected: 'Detectado',
    acceptSuggestions: 'aceptar sugerencias',
    removeSuggestion: (label: string) => `Quitar la sugerencia ${label}`,
    whereTo: '¿Dónde va?',
    whereToHint: 'Si lo resuelves en menos de 2 minutos, hazlo ya y pulsa H.',
    destinations: {
      A: { label: 'ASAP', hint: 'En este periodo' },
      B: { label: 'Backlog', hint: 'Más adelante' },
      G: { label: 'Algún día', hint: 'Quizá, sin compromiso' },
      S: { label: 'Seguimiento', hint: 'Lo hace otra persona' },
      H: { label: 'Hecho', hint: 'Lo he hecho ya' },
      X: { label: 'Eliminar', hint: 'No sirve' },
    },
    destinationName: (label: string, hint: string, badge?: string) =>
      `${label}${badge ? ` ${badge}` : ''}: ${hint}`,
    footer: (type: string) => [
      { key: 'J K', text: 'siguiente · anterior' },
      { key: 'Z', text: 'deshacer' },
      { key: 'I', text: `marcar como ${type} al enviar a ASAP` },
      { key: 'E', text: 'editar el título' },
    ],
    edited: 'Editado',
    accepted: 'Sugerencias aplicadas.',
    sent: (destination: string, next: string | null) =>
      next ? `Enviado a ${destination}. Ahora: ${next}` : `Enviado a ${destination}. Inbox vacío.`,
    undone: (title: string) => `Deshecho. De nuevo: ${title}`,
    taiOnlyAsap: 'Solo tareas en ASAP pueden ser TAI: se envía sin marcar.',
    noPeriod: 'No hay un periodo abierto: no se puede marcar la TAI.',
    error: 'No se ha podido aplicar la decisión. Vuelve a intentarlo.',
    empty: 'Inbox vacío. Buen trabajo.',
    backToInbox: 'Volver al Inbox',
    followUp: {
      title: 'Seguimiento',
      person: 'Esperando a',
      personHint: 'Elige una persona o escribe un nombre nuevo.',
      newPerson: (name: string) => `Se creará la persona «${name}».`,
      date: 'Fecha de seguimiento',
      confirm: 'Enviar a Seguimiento',
      cancel: 'Cancelar',
    },
    limit: {
      title: 'Límite de TAI',
      message: (count: number, max: number) =>
        `Ya tienes ${count} de ${max} TAI en este periodo. ¿Reemplazar la actual o añadir otra?`,
      replace: 'Reemplazar',
      addAnyway: 'Añadir igualmente',
      cancel: 'Cancelar',
    },
  },
  asap: {
    list: 'Tareas de la vista',
    chipsGroup: 'Contexto',
    all: 'Todos',
    noContext: 'Sin contexto',
    progress: 'Progreso del periodo',
    progressText: (done: number, total: number) => `${done} de ${total} hechas`,
    since: (day: string) => `Desde la revisión del ${day}`,
    complete: (title: string) => `Completar «${title}»`,
    reopen: (title: string) => `Reabrir «${title}»`,
    subtasks: (progress: string) => `Subtareas hechas: ${progress}`,
    toggleHighlight: (title: string, on: boolean) =>
      on ? `Quitar la TAI a «${title}»` : `Marcar «${title}» como TAI`,
    move: (title: string) => `Mover «${title}» a…`,
    moveTitle: 'Mover a…',
    taiTitle: 'TAI · Tarea de Alto Impacto',
    taiUsage: (count: number, max: number | null) =>
      max === null ? `${count} en este periodo` : `${count} de ${max} en este periodo`,
    doneSection: (n: number) => `Hechas en este periodo (${n})`,
    hidden: (n: number) => (n === 1 ? '1 tarea hecha, oculta' : `${n} tareas hechas, ocultas`),
    show: 'Mostrar',
    completed: 'Tarea completada',
    reopened: 'Tarea reabierta',
    movedTo: (stage: string) => `Movida a ${stage}`,
    taiWithdrawn: 'Ya no es TAI: solo las tareas en ASAP pueden serlo.',
    taiMarked: 'Marcada como TAI',
    taiRemoved: 'TAI quitada',
    taiOnlyAsap: 'Solo tareas en ASAP',
    undo: 'Deshacer',
    cancel: 'Cancelar',
    empty: 'No hay tareas en esta vista.',
    capped: (shown: number) => `Se muestran las ${shown} tareas abiertas más recientes; hay más.`,
    subtasksTitle: 'Subtareas abiertas',
    subtasksQuestion: (n: number) =>
      n === 1
        ? '¿Completar también la subtarea abierta?'
        : `¿Completar también las ${n} subtareas?`,
    withChildren: 'Completar también',
    onlyThis: 'Solo la tarea',
    options: 'Opciones de vista',
    error: 'No se ha podido guardar el cambio. Vuelve a intentarlo.',
  },
  /** Detail panel (f1-detail-panel). */
  detail: {
    /** Name of each taxonomy in the panel; unknown ones keep the template's name. */
    taxonomy: (key: string, name: string) =>
      key === 'area'
        ? 'Área'
        : key === 'context'
          ? 'Contextos'
          : key === 'topic'
            ? 'Temas'
            : key === 'person'
              ? 'Personas'
              : name,
    waitingOn: 'Esperando a',
    labels: {
      panel: 'Detalle de la tarea',
      close: 'Cerrar',
      back: 'Volver',
      title: 'Título',
      titleEmpty: 'El título no puede quedar vacío.',
      stage: 'Etapa',
      move: 'Mover a…',
      status: 'Estado',
      done: 'Hecha',
      pending: 'Pendiente',
      due: 'Vence',
      start: 'Visible desde',
      clearDate: 'Quitar la fecha',
      tai: 'TAI',
      followUpDate: 'Fecha de seguimiento',
      link: 'Dirección',
      openLink: 'Abrir',
      changeLink: 'Cambiar',
      noLink: 'Sin dirección',
      subtasks: 'Subtareas',
      addSubtask: 'Añadir subtarea',
      newSubtask: 'Título de la subtarea',
      openSubtask: (title: string) => `Abrir «${title}»`,
      completeSubtask: (title: string) => `Completar «${title}»`,
      reopenSubtask: (title: string) => `Reabrir «${title}»`,
      noSubtasks: 'Sin subtareas.',
      tagAdd: (label: string) => `Añadir a ${label}`,
      tagRemove: (name: string) => `Quitar ${name}`,
      tagCreate: (name: string) => `Crear «${name}»`,
      tagEmpty: 'Sin coincidencias',
      note: {
        label: 'Nota',
        toolbar: 'Formato de la nota',
        bold: 'Negrita',
        italic: 'Cursiva',
        bulletList: 'Lista con viñetas',
        orderedList: 'Lista numerada',
        blockquote: 'Cita',
        code: 'Código',
        link: 'Enlace',
        linkPrompt: 'Dirección del enlace (https://…)',
        placeholder: 'Escribe una nota',
        plainHint: 'Esta nota se edita como texto: tiene formato que el editor no admite.',
      },
    },
    open: (title: string) => `Abrir «${title}»`,
    gone: 'Esa tarea ya no está disponible.',
  },
  /** Type conversion (f1-type-conversion): «Más acciones», the link dialog and the notices. */
  convert: {
    menu: 'Más acciones',
    convertTo: 'Convertir en…',
    kinds: {
      item: 'Elemento',
      task: 'Tarea',
      project: 'Proyecto',
      note: 'Nota',
      page: 'Página',
      link: 'Enlace',
      file: 'Archivo',
    } as Record<string, string>,
    /** The kind with its article, for «Ahora es una nota». */
    article: {
      item: 'un elemento',
      task: 'una tarea',
      project: 'un proyecto',
      note: 'una nota',
      page: 'una página',
      link: 'un enlace',
    } as Record<string, string>,
    command: (kind: string) => `Convertir en ${kind.toLowerCase()}`,
    done: (withArticle: string) => `Ahora es ${withArticle}`,
    hidden: 'Ya no aparece en las vistas de tareas.',
    linkTitle: 'Convertir en enlace',
    changeLinkTitle: 'Cambiar la dirección',
    linkChanged: 'Dirección cambiada',
    url: 'Dirección (https://…)',
    submit: 'Convertir',
    save: 'Guardar',
    cancel: 'Cancelar',
    invalid: 'La dirección no es válida: usa una que empiece por http:// o https://.',
  },
  /** Backlog, Algún día and Seguimiento (f1-deferred-views); ASAP keeps its texts above. */
  lists: {
    noGroup: (groupBy: string) =>
      groupBy === 'tag:area'
        ? 'Sin área'
        : groupBy.startsWith('field:')
          ? 'Sin persona'
          : 'Sin contexto',
    chipsGroup: (groupBy: string) =>
      groupBy === 'tag:area' ? 'Área' : groupBy.startsWith('field:') ? 'Persona' : 'Contexto',
    empty: (viewKey: string) =>
      viewKey === 'backlog'
        ? 'No hay nada en Backlog.'
        : viewKey === 'someday'
          ? 'No hay nada en Algún día.'
          : viewKey === 'waiting'
            ? 'No esperas nada de nadie.'
            : 'No hay tareas en esta vista.',
    visibleFrom: (day: string) => `desde ${day}`,
    overdue: 'Vencido',
  },
  viewOptions: {
    title: 'Opciones de vista',
    close: 'Cerrar',
    layout: 'Disposición',
    layouts: [
      { key: 'list', label: 'Lista' },
      { key: 'outline', label: 'Esquema' },
      { key: 'kanban', label: 'Kanban' },
      { key: 'gallery', label: 'Galería' },
      { key: 'calendar', label: 'Calendario' },
      { key: 'table', label: 'Tabla' },
    ],
    comingLater: 'Las demás disposiciones llegan más adelante.',
    groupBy: 'Agrupar por',
    groups: (groupBy: string) =>
      groupBy === 'tag:area' ? 'Área' : groupBy.startsWith('field:') ? 'Persona' : 'Contexto',
    noGrouping: 'Ninguno',
    sort: 'Ordenar por',
    sorts: {
      due_then_manual: 'Vencimiento, luego manual',
      manual: 'Manual',
      start_then_newest: '«Visible desde», luego más recientes',
      newest: 'Más recientes',
      follow_up: 'Fecha de seguimiento',
    },
    filter: 'Filtro',
    filterSummary: (stage: string, visibleFromToday: boolean) =>
      `Etapa ${stage}${visibleFromToday ? ' · visible desde hoy' : ''}`,
    completedHeading: 'Tareas completadas',
    completed: {
      strike: { label: 'Tachar en su sitio', hint: 'Se quedan donde estaban, tachadas' },
      section: {
        label: 'Sección «Hechas»',
        hint: 'Se tachan y, al salir de la vista, bajan a una sección plegable',
      },
      hide: { label: 'Ocultar', hint: 'Desaparecen con un aviso para deshacer' },
    },
    window: 'Mostrar las hechas',
    windows: { period: 'Del periodo actual', today: 'De hoy', always: 'Siempre' },
    progressHeading: 'Progreso',
    progress: 'Barra de progreso del periodo',
    periodStart: 'El periodo empieza',
    periodStartValue: 'En la última revisión semanal',
    taiHeading: 'TAI · Tarea de Alto Impacto',
    taiEnabled: 'Permitir marcar TAI en esta vista',
    max: 'Máximo por periodo',
    unlimited: 'Sin límite',
    decrease: 'Reducir el máximo',
    increase: 'Aumentar el máximo',
    mode: 'Al superar el máximo',
    modes: { warn: 'Avisar', block: 'Impedir' },
    pinTop: 'Fijar la TAI arriba de la lista',
    taiHint:
      'Recomendado: una por periodo. La revisión semanal te pedirá elegirla y el cierre dirá si la cumpliste.',
  },
  rejections: {
    title: 'Cambios no guardados',
    acknowledge: 'Entendido',
    highlight: (message: string) => `No se ha marcado la TAI: ${message}`,
    forbidden: 'No tienes permiso para ese cambio; se ha deshecho.',
    generic: (message: string) => `Un cambio no se ha guardado: ${message}`,
  },
  views: {
    notFoundTitle: 'Vista no encontrada',
    notFound: (key: string) => `No tienes ninguna vista «${key}».`,
    comingSoon: (name: string) =>
      `La vista «${name}» llega en su propio cambio de F1; de momento su contenido está vacío.`,
    ritualComingSoon: (name: string) => `El ritual «${name}» llega en F2.`,
  },
  pwa: {
    updateAvailable: 'Hay una versión nueva de flow.',
    update: 'Actualizar',
    later: 'Más tarde',
    offlineReady: 'La app ya puede abrirse sin conexión.',
  },
  capture: {
    title: 'Cámara y micrófono (prueba)',
    intro:
      'Comprueba cámara, micrófono y dictado desde el navegador o la app instalada. Nada se sube ni se guarda como archivo.',
    environment: 'Entorno',
    installed: 'App instalada (pantalla completa)',
    browserTab: 'Pestaña del navegador',
    persisted: (value: boolean | null) =>
      value === null
        ? 'almacenamiento persistente: no disponible'
        : value
          ? 'almacenamiento persistente: concedido'
          : 'almacenamiento persistente: no concedido',
    photo: {
      title: 'Foto',
      take: 'Hacer foto',
      pick: 'Elegir de la fototeca',
      details: (w: number, h: number, kb: number, type: string) =>
        `${w} × ${h} px · ${kb} KB · ${type || 'tipo desconocido'}`,
      oriented: (w: number, h: number) => `orientación aplicada: ${w} × ${h} px`,
      openCamera: 'Abrir cámara (getUserMedia)',
      closeCamera: 'Cerrar cámara',
      cameraOn: (w: number, h: number) => `Cámara activa · ${w} × ${h}`,
    },
    audio: {
      title: 'Audio',
      record: 'Grabar 10 s',
      stop: 'Detener',
      recording: (s: number) => `Grabando… ${s} s`,
      details: (seconds: string, kb: number, type: string) =>
        `${seconds} · ${kb} KB · ${type || 'tipo desconocido'}`,
      unsupported: 'Este navegador no admite MediaRecorder.',
    },
    dictation: {
      title: 'Dictado del teclado',
      hint: 'Pulsa el micrófono del teclado de iOS y dicta; se guarda como una captura normal.',
      placeholder: 'Dicta o escribe aquí…',
      save: 'Guardar como bloque',
      saved: (title: string) => `Guardado: «${title}»`,
    },
    permissionDenied:
      'Permiso denegado. Actívalo en Ajustes → Safari → Cámara / Micrófono (o en Ajustes → flow si la app está instalada) y vuelve a intentarlo.',
    notFound: 'No se ha encontrado cámara o micrófono en este dispositivo.',
    notSupported: 'Este navegador no admite esta función.',
    error: (message: string) => `Error: ${message}`,
  },
  blocks: {
    title: 'Bloques',
    empty: 'No hay bloques todavía. Crea el primero.',
    newPlaceholder: 'Nueva tarea…',
    create: 'Crear',
    complete: 'Completar',
    reopen: 'Reabrir',
    rename: 'Editar título',
    done: 'Hecho',
    kind: {
      item: 'Elemento',
      task: 'Tarea',
      project: 'Proyecto',
    } as Record<string, string>,
  },
  /** Search page and results in the palette (f1-trash-search, design D6). */
  search: {
    title: 'Buscar',
    placeholder: 'Buscar en títulos, notas y etiquetas…',
    field: 'Texto que buscas',
    empty: (term: string) => `No hay resultados para «${term}».`,
    prompt: 'Escribe para buscar. Funciona sin conexión.',
    results: (n: number) => (n === 1 ? '1 resultado' : `${n} resultados`),
    took: (ms: number) => `${ms} ms`,
    heading: (label: string, count: number) => `${label} (${count})`,
    open: (title: string) => `Abrir «${title}»`,
    done: 'Hecha',
    more: (count: number) => `Mostrar ${count} más`,
    paletteGroup: 'Bloques',
    seeAll: 'Ver todos los resultados',
    groups: {
      task: 'Tareas',
      project: 'Proyectos',
      note: 'Notas',
      page: 'Páginas',
      link: 'Enlaces',
      item: 'Elementos',
      file: 'Archivos',
    } as Record<string, string>,
  },
  /** Trash (f1-trash-search, design D2, D3, D7). */
  trash: {
    title: 'Papelera',
    list: 'Elementos en la papelera',
    empty: 'La papelera está vacía.',
    retention: 'Lo que eliminas se borra para siempre a los 30 días.',
    truncated: 'Hay más elementos en la papelera de los que se muestran aquí.',
    restore: (title: string) => `Restaurar «${title}»`,
    restoreShort: 'Restaurar',
    purge: (title: string) => `Eliminar definitivamente «${title}»`,
    purgeShort: 'Eliminar definitivamente',
    offline: 'Necesita conexión',
    confirmTitle: '¿Eliminar para siempre?',
    confirm: (title: string, descendants: number) =>
      descendants > 0
        ? `«${title}» y ${descendants === 1 ? 'su elemento' : `sus ${descendants} elementos`} se borrarán para siempre. No se puede deshacer.`
        : `«${title}» se borrará para siempre. No se puede deshacer.`,
    confirmYes: 'Eliminar para siempre',
    cancel: 'Cancelar',
    purged: 'Eliminada para siempre',
    purgeError: 'No se ha podido eliminar. Vuelve a intentarlo con conexión.',
    meta: (descendants: number, deletedDay: string, daysLeft: number) =>
      [
        descendants > 0
          ? descendants === 1
            ? '1 elemento dentro'
            : `${descendants} elementos dentro`
          : null,
        `eliminada ${deletedDay}`,
        daysLeft === 0
          ? 'se borra hoy'
          : daysLeft === 1
            ? 'se borra mañana'
            : `se borra en ${daysLeft} días`,
      ]
        .filter(Boolean)
        .join(' · '),
    trashed: 'Eliminada. Ya no aparece en tus vistas',
    restored: 'Restaurada',
    delete: 'Eliminar',
    command: 'Eliminar',
    goTo: 'Papelera',
    today: 'hoy',
  },
  perf: {
    title: 'Medición de rendimiento (F0)',
    intro:
      'Ejecuta la consulta ASAP con lente de área sobre la base local y mide el recálculo de etiquetas efectivas y el guardado de una captura. Sin otras pestañas abiertas.',
    run: 'Ejecutar medición',
    running: 'Midiendo…',
    copy: 'Copiar JSON',
    copied: 'Copiado',
    needsData: 'La base local no tiene bloques todavía; espera a la primera sincronización.',
    views: 'Vistas reactivas de la pantalla de bloques (medición con vistas)',
  },
  sync: {
    connected: 'Conectado',
    disconnected: 'Sin conexión con el servidor',
    connecting: 'Conectando…',
    firstSync: 'Primera sincronización…',
    firstSyncTook: (seconds: number) =>
      `primera sincronización en ${seconds.toFixed(1).replace('.', ',')} s`,
    uploading: 'Subiendo…',
    downloading: 'Recibiendo…',
    pending: (n: number) =>
      n === 0 ? 'Todo sincronizado' : n === 1 ? '1 cambio pendiente' : `${n} cambios pendientes`,
    storage: {
      persisted: 'Almacenamiento persistente concedido',
      notPersisted:
        'Almacenamiento persistente no concedido (el navegador podría borrar datos sin uso)',
      unsupported: 'El navegador no permite solicitar almacenamiento persistente',
      memoryFallback:
        'Este navegador (o ventana privada) no permite guardar datos: los cambios no persistirán al cerrar.',
    },
    vfs: (name: string) => `SQLite: ${name}`,
    error: 'No se ha podido sincronizar.',
    retry: 'Reintentar',
  },
  theme: {
    legend: 'Apariencia',
    options: { light: 'Claro', dark: 'Oscuro', system: 'Sistema' },
    deviceOnly: 'Solo en este dispositivo',
  },
} as const;
